import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faTimes } from '@fortawesome/free-solid-svg-icons';
import { showToast } from '../Modais/Toast';

function getCookie(nome) {
    var nomeEQ = nome + "=";
    var ca = document.cookie.split(';');
    for(var i=0;i < ca.length;i++) {
        var c = ca[i];
        while (c.charAt(0)===' ') c = c.substring(1,c.length);
        if (c.indexOf(nomeEQ) === 0) return c.substring(nomeEQ.length,c.length);
    }
    return null;
}

const acoesDisponiveis = [
    { value: "acessar_sistema", label: "Acesso ao sistema" },
    { value: "alterar_registro", label: "Alteração de registro" },
    { value: "criar_grupo", label: "Criação de grupo" },
    { value: "criar_registro", label: "Criação de registro" },
    { value: "criptografar", label: "Criptografia" },
    { value: "deletar_grupo", label: "Exclusão de grupo" },
    { value: "deletar_registro", label: "Exclusão de registro" },
    { value: "descriptografar", label: "Descriptografia" },
    { value: "ver_senha", label: "Visualização de senha" },
    { value: "alterar_permissoes_registro", label: "Alteração de permissão de registro"},
    { value: "liberar_ip", label: "Liberação de IP" }
];

const Auditoria = ({ handleBackToCards, showToast }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filteredIds, setFilteredIds] = useState([]);
    const [pagination, setPagination] = useState({
        pagina: 1,
        porPagina: 50,
        total: 0,
        totalPaginas: 1
    });
    const [filters, setFilters] = useState({
        usuario: '',
        dataInicio: '',
        dataFim: '',
        acao: '',
        detalhes: ''
    });
    const [showFilters, setShowFilters] = useState(true);
    const [resultsLimited, setResultsLimited] = useState(false);
    const timeoutRef = useRef(null);
    const dateTimeoutRef = useRef(null);
    const [tempDates, setTempDates] = useState({ dataInicio: '', dataFim: '' }); // Estados temporários para datas
    const [dateToastShown, setDateToastShown] = useState(false);

    // Busca os IDs filtrados e o total de registros
    const fetchFilteredIds = useCallback(async () => {

        setLoading(true);
        try {
            const token = getCookie("info");
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auditorias/buscar`, {
                method: 'POST',
                headers: {
                    'Authority': token,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    data: {
                        inicio: filters.dataInicio,
                        fim: filters.dataFim
                    },
                    acao: filters.acao,
                    usuario: filters.usuario,
                    detalhes: filters.detalhes 
                })
            });

            const result = await response.json();
            
            if (result.status === "ok") {
                setFilteredIds(result.ids_filtrados || []);
                setResultsLimited(result.limitado || false);
                const totalPaginas = Math.ceil(result.total / pagination.porPagina);
                setPagination(prev => ({
                    ...prev,
                    total: result.total,
                    totalPaginas: totalPaginas,
                    pagina: 1
                }));
                return result.ids_filtrados || [];
            } else {
                showToast(result.mensagem || 'Erro ao aplicar filtros', 'error');
                return [];
            }
        } catch (error) {
            console.error('Erro ao buscar IDs filtrados:', error);
            showToast('Erro ao aplicar filtros', 'error');
            return [];
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.porPagina, showToast]);

    // Busca os registros para a página atual
    const fetchPageData = useCallback(async (ids, page) => {
        setLoading(true);
        try {
            const token = getCookie("info");
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auditorias/buscar_por_ids`, {
                method: 'POST',
                headers: {
                    'Authority': token,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ids: ids,
                    pagina: page,
                    por_pagina: pagination.porPagina
                })
            });

            const result = await response.json();
            
            if (result.status === "ok") {
                setLogs(result.lista);
                setPagination(prev => ({
                    ...prev,
                    pagina: page
                }));
            } else {
                showToast(result.mensagem || 'Erro ao carregar página', 'error');
            }
        } catch (error) {
            console.error('Erro ao buscar dados da página:', error);
            showToast('Erro ao carregar página', 'error');
        } finally {
            setLoading(false);
        }
    }, [pagination.porPagina, showToast]);

    // Função debounce para aplicar filtros (exceto datas)
    const applyFiltersWithDebounce = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        
        timeoutRef.current = setTimeout(async () => {
            // Verifica se apenas uma data foi preenchida
            const hasOnlyOneDate = (filters.dataInicio && !filters.dataFim) || (!filters.dataInicio && filters.dataFim);
            
            if (hasOnlyOneDate) {
                if (!dateToastShown) {
                    showToast("Insira o range de datas completo", "error");
                    setDateToastShown(true);
                }
                return;
            }

            // Se chegou aqui, ou ambas estão preenchidas ou nenhuma está
            setDateToastShown(false); // Reseta o estado quando temos range completo ou vazio
            
            const ids = await fetchFilteredIds();
            if (ids.length > 0) {
                await fetchPageData(ids, 1);
            } else {
                setLogs([]);
            }
        }, 500);
    }, [fetchFilteredIds, fetchPageData, filters.dataInicio, filters.dataFim, dateToastShown, showToast]);

    // Função debounce especial para datas (com delay maior)
    const handleDateSelection = useCallback((dateType, value) => {
        if (dateTimeoutRef.current) {
            clearTimeout(dateTimeoutRef.current);
        }
        
        // Atualiza o estado temporário imediatamente para feedback visual
        setTempDates(prev => ({ ...prev, [dateType]: value }));
        
        // Agenda a atualização do filtro e a busca após 1.5 segundos
        dateTimeoutRef.current = setTimeout(() => {
            setFilters(prev => ({ ...prev, [dateType]: value }));
        }, 1500);
    }, []);

    useEffect(() => {
    // Quando as datas mudam, reseta o controle do toast
    setDateToastShown(false);
}, [filters.dataInicio, filters.dataFim]);

    // Efeito para aplicar filtros com debounce (exceto datas)
    useEffect(() => {
        // Não dispara para mudanças nas datas
        if (filters.dataInicio !== tempDates.dataInicio || filters.dataFim !== tempDates.dataFim) return;
        
        applyFiltersWithDebounce();
        
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [filters, tempDates, applyFiltersWithDebounce]);

    // Cleanup dos timeouts
    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (dateTimeoutRef.current) clearTimeout(dateTimeoutRef.current);
        };
    }, []);

    // Quando a página muda, busca os dados para a nova página
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPaginas) {
            fetchPageData(filteredIds, newPage);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const clearFilter = (filterName) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: ''
        }));
        if (filterName === 'dataInicio' || filterName === 'dataFim') {
            setTempDates(prev => ({ ...prev, [filterName]: '' }));
        }
    };

    const clearDateRange = () => {
        setFilters(prev => ({
            ...prev,
            dataInicio: '',
            dataFim: ''
        }));
        setTempDates({ dataInicio: '', dataFim: '' });
    };

    const parseAcao = (acao) => {
        const acaoEncontrada = acoesDisponiveis.find(a => a.value === acao);
        return acaoEncontrada ? acaoEncontrada.label : acao;
    };

    return (
        <section className="auditoria-container">
            <div className="auditoria-header">
                <h2>Registros de Auditoria</h2>
                <div className="auditoria-actions">
                    <button 
                        className="btn-filter"
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <FontAwesomeIcon icon={faFilter} /> {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                    </button>
                </div>
            </div>

            {showFilters && (
                <div className="filters-panel">
                    <div className="filter-group">
                        <label>Usuário</label>
                        <div className="filter-input-container">
                            <input 
                                type="text" 
                                name="usuario" 
                                value={filters.usuario}
                                onChange={(e) => {
                                    if (e.target.value.length > 25) {
                                        showToast("O nome não pode ter mais de 25 caracteres", "warning");
                                        return; // Impede a atualização se exceder
                                    }
                                    handleFilterChange(e);
                                }}
                                placeholder="Digite o nome do usuário"
                            />
                            {filters.usuario && (
                                <button 
                                    className="clear-filter-btn"
                                    onClick={() => clearFilter('usuario')}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    </div>
                    
                    <div className="filter-group period-group">
                        <div className="filter-input-container period-input-container">
                            <div className="date-range-wrapper">
                                <div className="date-input-wrapper">
                                    <label>De:</label>
                                    <input 
                                        type="date" 
                                        name="dataInicio" 
                                        value={tempDates.dataInicio}
                                        onChange={(e) => handleDateSelection('dataInicio', e.target.value)}
                                        max={tempDates.dataFim || undefined}
                                        className="date-input"
                                    />
                                    
                                </div>
                                
                                <div className="date-input-wrapper">
                                    <label>Até:</label>
                                    <input 
                                        type="date" 
                                        name="dataFim" 
                                        value={tempDates.dataFim}
                                        onChange={(e) => handleDateSelection('dataFim', e.target.value)}
                                        min={tempDates.dataInicio || undefined}
                                        className="date-input"
                                    />
                                    
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="filter-group">
                        <label>Ação</label>
                        <select
                            name="acao" 
                            value={filters.acao}
                            onChange={handleFilterChange}
                        >
                            <option value="">Todas as ações</option>
                            {acoesDisponiveis.map((acao) => (
                                <option key={acao.value} value={acao.value}>
                                    {acao.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    <div className="filter-group">
                        <label>Detalhes</label>
                        <div className="filter-input-container">
                            <input 
                                type="text" 
                                name="detalhes" 
                                value={filters.detalhes}
                                onChange={(e) => {
                                    if (e.target.value.length > 25) {
                                        showToast("Digite até 25 caracteres", "warning");
                                        return; // Impede a atualização se exceder
                                    }
                                    handleFilterChange(e);
                                }}
                                placeholder="Buscar palavra"
                            />
                            {filters.detalhes && (
                                <button 
                                    className="clear-filter-btn"
                                    onClick={() => clearFilter('detalhes')}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="loading">Carregando registros de auditoria...</div>
            ) : (
                <div className="auditoria-table">
                    {resultsLimited && (
                        <div className="results-limited-notice">
                            <p>Mostrando apenas os registros mais recentes. Aplique filtros para refinar sua busca.</p>
                        </div>
                    )}

                    <div className="table-header">
                        <div className="header-cell">Usuário</div>
                        <div className="header-cell">Data/Hora</div>
                        <div className="header-cell">Ação</div>
                        <div className="header-cell">Detalhes</div>
                    </div>

                    <div className="table-body">
                        {logs.length > 0 ? (
                            logs.map((log, index) => (
                                <div 
                                    key={`${log.data}-${log.hora}-${log.nome}-${index}`}
                                    className="log-card"
                                >
                                    <div className="log-cell">
                                        <div className="user-name">{log.nome}</div>
                                        <div className="user-email">{log.email}</div>
                                    </div>

                                    <div className="log-cell">
                                        <div className="log-date">{log.data}</div>
                                        <div className="log-time">{log.hora}</div>
                                    </div>
                                    
                                    <div className="log-cell">
                                        <div className="log-action">{parseAcao(log.acao)}</div>
                                    </div>
                                    
                                    <div className="log-cell">
                                        <div className="log-details">{log.log}</div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="no-results">
                                Nenhum resultado encontrado com os filtros aplicados
                            </div>
                        )}
                    </div>

                    {pagination.totalPaginas > 1 && (
                        <div className="pagination">
                            <button 
                                onClick={() => handlePageChange(pagination.pagina - 1)}
                                disabled={pagination.pagina === 1}
                            >
                                ◀
                            </button>
                            
                            <span>
                                Página {pagination.pagina} de {pagination.totalPaginas} 
                            </span>
                            
                            <button 
                                onClick={() => handlePageChange(pagination.pagina + 1)}
                                disabled={pagination.pagina === pagination.totalPaginas}
                            >
                                ▶
                            </button>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

export default Auditoria;